import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/registrations/payment-status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(response.data);
        } else if (response.data.status === "expired") {
          setStatus("expired");
        } else if (attemptsRef.current < 5) {
          // Continue polling
          attemptsRef.current += 1;
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus("timeout");
        }
      } catch (err) {
        console.error("Payment status check error:", err);
        if (attemptsRef.current < 5) {
          attemptsRef.current += 1;
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full p-8 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <div className="animate-spin h-16 w-16 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-6"></div>
            <h2 className="text-2xl font-semibold text-white mb-2">Verifying Payment</h2>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Payment Successful!</h2>
            <p className="text-slate-400 mb-6">Your registration for LIRSS Conference 2026 is now confirmed.</p>
            
            {paymentData && (
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-slate-300 mb-2">
                  <span>Amount Paid:</span>
                  <span className="font-semibold text-white">£{paymentData.amount?.toFixed(2)}</span>
                </div>
              </div>
            )}

            <p className="text-emerald-400 text-sm mb-6">A confirmation email has been sent to your registered email address.</p>
            
            <div className="space-y-3">
              <Link 
                to="/"
                className="block w-full bg-emerald-500 text-white font-semibold py-3 px-6 rounded-xl hover:bg-emerald-600 transition-all"
                data-testid="register-another-btn"
              >
                Register Another Delegate
              </Link>
            </div>
          </div>
        )}

        {status === "expired" && (
          <div data-testid="payment-expired">
            <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Session Expired</h2>
            <p className="text-slate-400 mb-6">Your payment session has expired. Please try again.</p>
            <Link 
              to="/"
              className="block w-full bg-emerald-500 text-white font-semibold py-3 px-6 rounded-xl hover:bg-emerald-600 transition-all"
            >
              Try Again
            </Link>
          </div>
        )}

        {(status === "error" || status === "timeout") && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Something Went Wrong</h2>
            <p className="text-slate-400 mb-6">We couldn't verify your payment. If you completed the payment, please check your email for confirmation or contact support.</p>
            <div className="space-y-3">
              <Link 
                to="/"
                className="block w-full bg-emerald-500 text-white font-semibold py-3 px-6 rounded-xl hover:bg-emerald-600 transition-all"
              >
                Back to Registration
              </Link>
              <a 
                href="mailto:libssorg@gmail.com"
                className="block w-full bg-slate-700 text-white font-semibold py-3 px-6 rounded-xl hover:bg-slate-600 transition-all"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
